import React, { useState, useEffect, useContext } from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Box,
  Flex,
  Button,
  HStack,
  useDisclosure,
  Select,
  Input,
  Textarea,
  Icon,
  Switch,
  Divider,
  Wrap,
  useColorMode,
  useColorModeValue,
  useBreakpointValue,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
} from "@chakra-ui/react";
import DeleteDialog from "./ui/DeleteDialog";
import ImageUploader from "./ImageUploader";
import InterestEditPill from "./ui/InterestEditPill";
import SettingsProfileHeader from "./SettingsProfileHeader";
import { googleLogout } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { HiExternalLink } from "react-icons/hi";
import { GetAllInterestsByUserId, UpdateUser, GetUserById, GetUserImages } from "../services/backend";
import { useToast } from "@chakra-ui/react";
import { AuthContext } from "../store/auth-context";
import { SETTINGS_MENU_EVENT } from "./MobileTopBar";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguageModal } from "../context/LanguageModalContext";
import LanguageMenuTrigger from "./LanguageMenuTrigger";
import SettingsPremiumBanner from "./SettingsPremiumBanner";
import SettingsMembership from "./SettingsMembership";
import { SunIcon, MoonIcon } from "@chakra-ui/icons";
import { INTEREST_OPTIONS } from "../constants/interests";
import { getProfileCompletion } from "../util/profileCompletion";

const Settings = () => {
  const { t } = useTranslation();
  const { openLanguageModal } = useLanguageModal();
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { colorMode, toggleColorMode } = useColorMode();
  const {
    isOpen: isMenuOpen,
    onOpen: openMenu,
    onClose: closeMenu,
  } = useDisclosure();
  const [tabIndex, setTabIndex] = useState(0);
  const isMobileShell = useBreakpointValue({ base: true, lg: false }, { ssr: false }) ?? false;

  useEffect(() => {
    const handler = () => openMenu();
    window.addEventListener(SETTINGS_MENU_EVENT, handler);
    return () => window.removeEventListener(SETTINGS_MENU_EVENT, handler);
  }, [openMenu]);
  const cancelRef = React.useRef();
  const [bio, setBio] = useState("");
  const [selectedInterests, setSelectedInterests] = useState(() => new Set());
  let storedUser = null;
  try {
    const raw = localStorage.getItem("user");
    storedUser = raw ? JSON.parse(raw) : null;
  } catch {
    storedUser = null;
  }
  const userId = storedUser?.id;
  const user = storedUser;
  const AuthCtx = useContext(AuthContext);
  const [occupation, setOccupation] = useState(storedUser?.occupation ?? "");
  const [education, setEducation] = useState(storedUser?.education ?? "");
  const [lifeStyleData, setLifeStyleData] = useState({
    drinking: storedUser?.drinking ?? "",
    smoking: storedUser?.smoking ?? "",
    workout: storedUser?.workout ?? "",
    religion: storedUser?.religion ?? "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(storedUser?.profileImageUri ?? "");
  const [photoCount, setPhotoCount] = useState(0);
  const [profileMeta, setProfileMeta] = useState({
    gender: storedUser?.gender ?? storedUser?.preferences?.gender ?? "",
    birthDate: storedUser?.birthDate || storedUser?.birth_date || "",
  });
  const isProfileValid = Boolean((bio ?? "").trim());

  const completion = getProfileCompletion({
    bio,
    gender: profileMeta.gender,
    birthDate: profileMeta.birthDate,
    photoCount,
    occupation,
    education,
    drinking: lifeStyleData.drinking,
    smoking: lifeStyleData.smoking,
    workout: lifeStyleData.workout,
    religion: lifeStyleData.religion,
    interestCount: selectedInterests.size,
  });

  useEffect(() => {
    if (!completion.isComplete) return;
    try {
      localStorage.removeItem("payinapo:finish-profile-banner");
      localStorage.setItem("payinapo:finish-profile-banner-dismissed", "1");
    } catch {
      /* ignore */
    }
  }, [completion.isComplete]);

  const shellBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const shellBg = useColorModeValue("rgba(255,255,255,0.85)", "rgba(18,18,20,0.65)");
  const shellShadow = useColorModeValue("0 8px 40px rgba(15,23,42,0.06)", "0 12px 48px rgba(0,0,0,0.35)");
  const tabListBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const tabIdle = useColorModeValue("gray.600", "whiteAlpha.700");
  const tabHoverBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const sectionTitleColor = useColorModeValue("gray.500", "whiteAlpha.500");
  const panelBg = useColorModeValue("rgba(255,255,255,0.95)", "rgba(12,12,14,0.75)");
  const cardHeaderBorder = useColorModeValue("gray.100", "whiteAlpha.100");
  const sectionBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const sectionBg = useColorModeValue("rgba(255,255,255,0.75)", "rgba(255,255,255,0.04)");
  const sectionTint = useColorModeValue("brand.50", "rgba(52,211,153,0.1)");
  const sectionTintBlue = useColorModeValue("blue.50", "rgba(59,130,246,0.1)");
  const sectionTintYellow = useColorModeValue("yellow.50", "rgba(234,179,8,0.08)");
  const inputBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const inputColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const helpPanelBg = useColorModeValue("blue.50", "rgba(59,130,246,0.1)");
  const helpInnerBg = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const dangerPanelBg = useColorModeValue("red.50", "rgba(239,68,68,0.1)");
  const dangerBorder = useColorModeValue("red.100", "whiteAlpha.100");
  const profileHeaderBorder = useColorModeValue("brand.100", "whiteAlpha.100");
  const stickyBarBg = useColorModeValue("rgba(255,255,255,0.96)", "#000000");
  const stickyBarFadeGradient = useColorModeValue(
    "linear(to-t, rgba(255,255,255,0.96), rgba(255,255,255,0))",
    "linear(to-t, rgba(0,0,0,0.95), rgba(0,0,0,0))"
  );
  const drawerContentBg = useColorModeValue("rgba(255,255,255,0.98)", "rgba(14,14,16,0.98)");
  const selectFocus = {
    borderColor: "brand.400",
    boxShadow: "0 0 0 1px var(--chakra-colors-brand-400)",
  };
  const nativeSelectOptionSx = useColorModeValue(
    {},
    {
      "& option, & optgroup": {
        backgroundColor: "#161618",
        color: "rgba(255, 255, 255, 0.92)",
      },
    }
  );

  useEffect(() => {
    if (!userId) return;
    const fetchInterests = async () => {
      try {
        const data = await GetAllInterestsByUserId(userId);
        const names = (Array.isArray(data) ? data : [])
          .map((i) => (typeof i === "string" ? i : i?.interestName || i?.name))
          .filter(Boolean);
        setSelectedInterests(new Set(names));
      } catch (error) {
        console.error("Failed to fetch interests:", error);
      }
    };
    fetchInterests();
  }, [userId]);

  const handleUpdateInterests = (interest) => {
    setSelectedInterests((prev) => {
      const next = new Set(prev);
      if (next.has(interest)) next.delete(interest);
      else next.add(interest);
      return next;
    });
  };
  useEffect(() => {
    if (!userId) return;
    async function loadUserProfile() {
      try {
        const u = await GetUserById(userId);
        setBio(u?.bio ?? "");
        if (u?.profileImageUri) setAvatarUrl(u.profileImageUri);
        setOccupation((prev) => prev || u?.occupation || u?.preferences?.occupation || "");
        setEducation((prev) => prev || u?.education || "");
        setLifeStyleData((prev) => ({
          drinking: prev.drinking || u?.drinking || u?.preferences?.drinking || "",
          smoking: prev.smoking || u?.smoking || u?.preferences?.smoking || "",
          workout: prev.workout || u?.workout || u?.preferences?.workout || "",
          religion: prev.religion || u?.religion || u?.preferences?.religion || "",
        }));
        setProfileMeta({
          gender: u?.gender || u?.preferences?.gender || "",
          birthDate: u?.birthDate || u?.birth_date || "",
        });
      } catch (error) {
        console.error("Failed to load profile bio:", error);
      }
    }
    async function loadPhotos() {
      try {
        const imgs = await GetUserImages(userId);
        setPhotoCount(Array.isArray(imgs) ? imgs.length : 0);
      } catch (error) {
        console.error("Failed to load profile photos:", error);
      }
    }
    loadUserProfile();
    loadPhotos();
  }, [userId]);

  const handlePhotosChanged = (images) => {
    setPhotoCount(Array.isArray(images) ? images.length : 0);
  };
  const handleAvatarUpdated = (uri) => {
    setAvatarUrl(uri);
    const updated = { ...user, profileImageUri: uri };
    localStorage.setItem("user", JSON.stringify(updated));
    AuthCtx.authenticateUser(updated);
  };

  const handleBioOnchange = (e) => {
    setBio(e.target.value);
  };

  const handleLogout = () => {
    googleLogout();
    navigate("/");
    localStorage.removeItem("user");
    AuthCtx.logoutUser();
  };

  const handleOccupationOnchange = (e) => {
    setOccupation(e.target.value);
  };

  const handleEducationOnchange = (e) => {
    setEducation(e.target.value);
  };

  const handleLifeStyleChange = (e) => {
    const { name, value } = e.target;
    setLifeStyleData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    if (!userId) return;
    setIsSaving(true);
    try {
      const data = {
        Id: userId,
        Bio: bio,
        Education: education,
        Occupation: occupation,
        Drinking: lifeStyleData.drinking,
        Smoking: lifeStyleData.smoking,
        Workout: lifeStyleData.workout,
        Religion: lifeStyleData.religion,
      };

      await UpdateUser(data);
      toast({
        description: t("settings.profileUpdated"),
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });

      const userFromDb = await GetUserById(userId);
      localStorage.setItem("user", JSON.stringify(userFromDb));
    } catch (err) {
      console.log(err.message);
      toast({
        description: t("settings.profileUpdateFailed"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    }
    setIsSaving(false);
  };

  const tabBase = {
    py: 3,
    px: 4,
    fontWeight: "medium",
    fontSize: "sm",
    color: tabIdle,
    borderLeft: "4px solid",
    borderColor: "transparent",
    borderRadius: "md",
    bg: tabListBg,
    transition: "all 0.2s",
    _hover: { bg: tabHoverBg, color: titleColor },
  };

  const tabSelectedProfile = {
    bg: sectionTint,
    color: titleColor,
    borderLeft: "4px solid",
    borderColor: "brand.500",
    fontWeight: "semibold",
  };

  const tabSelectedMembership = {
    bg: sectionTintYellow,
    color: titleColor,
    borderLeft: "4px solid",
    borderColor: "yellow.500",
    fontWeight: "semibold",
  };

  const tabSelectedHelp = {
    bg: sectionTint,
    color: titleColor,
    borderLeft: "4px solid",
    borderColor: "brand.400",
    fontWeight: "semibold",
  };

  const tabSelectedDelete = {
    bg: dangerPanelBg,
    color: titleColor,
    borderLeft: "4px solid",
    borderColor: "red.500",
    fontWeight: "semibold",
  };

  const sectionShellProps = {
    p: 4,
    borderRadius: "xl",
    borderWidth: "1px",
    borderColor: sectionBorder,
    backdropFilter: "blur(8px)",
  };

  const formatSectionLabel = (label) =>
    typeof label === "string" ? label.toLocaleUpperCase() : label;

  const sectionTitleProps = {
    fontSize: "xs",
    fontWeight: "semibold",
    color: sectionTitleColor,
    mb: 3,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  };

  const sidebarPanel = (
    <Box
      borderRadius={{ base: "0", lg: "2xl" }}
      borderWidth={{ base: 0, lg: "1px" }}
      borderColor={shellBorder}
      bg={{ base: "transparent", lg: shellBg }}
      backdropFilter={{ base: "none", lg: "blur(16px)" }}
      boxShadow={{ base: "none", lg: shellShadow }}
      overflow="hidden"
    >
      {/* Profile header */}
      <Box px={{ base: 0, md: 3 }} pt={4} pb={3}>
        <Box
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={profileHeaderBorder}
          bg={panelBg}
          overflow="hidden"
        >
          <SettingsProfileHeader
            userId={userId}
            user={user}
            avatarUrl={avatarUrl}
            onAvatarUpdated={handleAvatarUpdated}
            completionPercent={completion.percent}
            isProfileFullyComplete={completion.isComplete}
          />
        </Box>
      </Box>

      <Divider borderColor={shellBorder} />

      <SettingsPremiumBanner />

      {/* Navigation tabs */}
      <TabList flexDirection="column" gap={1} p={3}>
        <Tab {...tabBase} _selected={tabSelectedProfile}>
          {t("settings.profileDetails")}
        </Tab>
        <Tab {...tabBase} _selected={tabSelectedHelp}>
          {t("settings.helpSupport")}
        </Tab>
        <Tab {...tabBase} _selected={tabSelectedMembership}>
          {t("settings.membership")}
        </Tab>
        <Tab {...tabBase} _selected={tabSelectedDelete}>
          {t("settings.deleteAccount")}
        </Tab>
      </TabList>

      <Divider borderColor={shellBorder} />

      {/* Preferences */}
      <Box px={3} py={3}>
        <Flex
          as="label"
          htmlFor="settings-dark-mode-toggle"
          align="center"
          justify="space-between"
          gap={3}
          px={3}
          py={2.5}
          borderRadius="lg"
          cursor="pointer"
          _hover={{ bg: tabHoverBg }}
          transition="background 0.15s"
        >
          <Flex align="center" gap={3} flex={1} minW={0} lineHeight={1}>
            <Flex
              align="center"
              justify="center"
              flexShrink={0}
              w="20px"
              h="20px"
              aria-hidden
            >
              <Icon
                as={colorMode === "light" ? MoonIcon : SunIcon}
                boxSize="18px"
                color="brand.500"
                display="block"
              />
            </Flex>
            <Text
              as="span"
              fontSize="sm"
              fontWeight="medium"
              color={titleColor}
              lineHeight="1"
              m={0}
            >
              {t("common.darkMode")}
            </Text>
          </Flex>
          <Switch
            id="settings-dark-mode-toggle"
            isChecked={colorMode === "dark"}
            onChange={toggleColorMode}
            colorScheme="green"
            size="md"
          />
        </Flex>
      </Box>

      <Divider borderColor={shellBorder} />

      <Box px={3} py={3}>
        <LanguageMenuTrigger
          onClick={openLanguageModal}
          w="100%"
          justifyContent="flex-start"
          px={3}
          py={2.5}
        />
      </Box>

      <Divider borderColor={shellBorder} />

      {/* Logout */}
      <Box px={3} py={3}>
        <Button
          onClick={handleLogout}
          width="100%"
          justifyContent="center"
          leftIcon={
            <Box as="span" display="inline-flex">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </Box>
          }
          variant="outline"
          borderColor="brand.300"
          color="brand.600"
          fontWeight="semibold"
          _hover={{ bg: sectionTint, borderColor: "brand.400" }}
        >
          {t("common.logout")}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        w="100%"
        pb={{ base: "calc(160px + env(safe-area-inset-bottom, 0px))", lg: 0 }}
      >
        <Tabs
          variant="unstyled"
          orientation="vertical"
          width="100%"
          index={tabIndex}
          onChange={(idx) => {
            setTabIndex(idx);
            if (isMobileShell) closeMenu();
          }}
        >
          <Flex width="100%" direction={{ base: "column", md: "row" }} gap={6}>
            {!isMobileShell && (
              <Box width={{ base: "100%", md: "280px" }} flexShrink={0}>
                {sidebarPanel}
              </Box>
            )}

            <Box flex={1} minW={0}>
              {/* Mobile: tabs live in the drawer — also show a strip so Membership is obvious. */}
              {isMobileShell ? (
                <Flex
                  gap={2}
                  mb={4}
                  overflowX="auto"
                  css={{
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {[
                    t("settings.profileDetails"),
                    t("settings.helpSupport"),
                    t("settings.membership"),
                    t("settings.deleteAccount"),
                  ].map((label, idx) => {
                    const selected = tabIndex === idx;
                    return (
                      <Button
                        key={label}
                        type="button"
                        size="sm"
                        flexShrink={0}
                        borderRadius="full"
                        fontWeight={selected ? "semibold" : "medium"}
                        bg={selected ? "brand.500" : tabListBg}
                        color={selected ? "gray.900" : tabIdle}
                        borderWidth="1px"
                        borderColor={selected ? "brand.500" : sectionBorder}
                        _hover={{ bg: selected ? "brand.400" : tabHoverBg }}
                        onClick={() => setTabIndex(idx)}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </Flex>
              ) : null}

              <TabPanels>
                <TabPanel p={0}>
                  <Box
                    borderRadius="2xl"
                    borderWidth="1px"
                    borderColor={shellBorder}
                    bg={panelBg}
                    backdropFilter="blur(12px)"
                    overflow="hidden"
                    boxShadow={shellShadow}
                  >
                    <Flex
                      px={{ base: 4, md: 6 }}
                      py={4}
                      borderBottomWidth="1px"
                      borderColor={cardHeaderBorder}
                      align={{ base: "stretch", md: "center" }}
                      justify="space-between"
                      gap={4}
                      direction={{ base: "column", md: "row" }}
                    >
                      <Box minW={0} flex={1}>
                        <Text fontSize="sm" fontWeight="semibold" color={titleColor}>
                          {t("settings.profileDetails")}
                        </Text>
                        <Text fontSize="xs" color={muted}>
                          {t("settings.editProfileSubtitle")}
                        </Text>
                      </Box>
                      <Button
                        isLoading={isSaving}
                        loadingText={t("common.saving")}
                        size="sm"
                        isDisabled={!isProfileValid}
                        bgGradient="linear(to-r, brand.500, brand.600)"
                        color="white"
                        _hover={{ opacity: 0.92, transform: "translateY(-1px)" }}
                        _active={{ transform: "translateY(0)" }}
                        _disabled={{ opacity: 0.5, cursor: "not-allowed", transform: "none" }}
                        onClick={handleSaveChanges}
                        boxShadow="0 4px 14px rgba(52,211,153,0.3), 0 8px 22px rgba(253,224,71,0.1)"
                        display={{ base: "none", lg: "inline-flex" }}
                      >
                        {t("common.saveChanges")}
                      </Button>
                    </Flex>

                    <Box p={{ base: 4, md: 6 }}>
                      <Flex direction="column" gap={6}>
                        <Box {...sectionShellProps} bg={sectionTint}>
                          <Text {...sectionTitleProps}>{formatSectionLabel(t("settings.aboutMe"))}</Text>
                          <Textarea
                            value={bio}
                            onChange={handleBioOnchange}
                            placeholder={t("settings.aboutPlaceholder")}
                            size="sm"
                            color={inputColor}
                            bg={inputBg}
                            borderColor={inputBorder}
                            _placeholder={{ color: muted }}
                            _focus={selectFocus}
                            minH="100px"
                          />
                        </Box>

                        <Flex direction={{ base: "column", md: "row" }} gap={4}>
                          <Box {...sectionShellProps} bg={sectionBg} flex={1}>
                            <Text {...sectionTitleProps}>{formatSectionLabel(t("settings.occupation"))}</Text>
                            <Input
                              size="sm"
                              value={occupation}
                              onChange={handleOccupationOnchange}
                              placeholder={t("settings.occupationPlaceholder")}
                              color={inputColor}
                              bg={inputBg}
                              borderColor={inputBorder}
                              _placeholder={{ color: muted }}
                              _focus={selectFocus}
                            />
                          </Box>
                          <Box {...sectionShellProps} bg={sectionBg} flex={1}>
                            <Text {...sectionTitleProps}>{formatSectionLabel(t("settings.education"))}</Text>
                            <Select
                              size="sm"
                              placeholder={t("settings.educationPlaceholder")}
                              value={education}
                              onChange={handleEducationOnchange}
                              color={inputColor}
                              bg={inputBg}
                              borderColor={inputBorder}
                              _focus={selectFocus}
                              sx={nativeSelectOptionSx}
                            >
                              <option value="High School">High School</option>
                              <option value="Some College">Some College</option>
                              <option value="Associate">Associate</option>
                              <option value="Masters degree">Masters degree</option>
                              <option value="Doctorate">Doctorate</option>
                              <option value="Trade School">Trade School</option>
                              <option value="Other">Other</option>
                            </Select>
                          </Box>
                        </Flex>

                        <Box {...sectionShellProps} bg={sectionTintYellow}>
                          <Text {...sectionTitleProps}>{formatSectionLabel(t("settings.lifestyleTitle"))}</Text>
                          <Flex direction="column" gap={4}>
                            <Flex direction={{ base: "column", md: "row" }} gap={4}>
                              <Box flex={1}>
                                <Text fontSize="xs" color={muted} mb={1}>
                                  {t("settings.drinking")}
                                </Text>
                                <Select
                                  size="sm"
                                  placeholder={t("settings.placeholderDrinking")}
                                  name="drinking"
                                  value={lifeStyleData.drinking}
                                  onChange={handleLifeStyleChange}
                                  color={inputColor}
                                  bg={inputBg}
                                  borderColor={inputBorder}
                                  _focus={selectFocus}
                                  sx={nativeSelectOptionSx}
                                >
                                  <option value="Never">Never</option>
                                  <option value="Occasinally">Occasionally</option>
                                  <option value="Socially">Socially</option>
                                  <option value="Regularly">Regularly</option>
                                  <option value="Prefer not to say">Prefer not to say</option>
                                </Select>
                              </Box>
                              <Box flex={1}>
                                <Text fontSize="xs" color={muted} mb={1}>
                                  {t("settings.smoking")}
                                </Text>
                                <Select
                                  size="sm"
                                  placeholder={t("settings.placeholderSmoking")}
                                  name="smoking"
                                  value={lifeStyleData.smoking}
                                  onChange={handleLifeStyleChange}
                                  color={inputColor}
                                  bg={inputBg}
                                  borderColor={inputBorder}
                                  _focus={selectFocus}
                                  sx={nativeSelectOptionSx}
                                >
                                  <option value="Non-smoker">Non-smoker</option>
                                  <option value="Occasionally">Occasionally</option>
                                  <option value="Social smoker">Social smoker</option>
                                  <option value="Regular smoker">Regular smoker</option>
                                  <option value="Trying to quit">Trying to quit</option>
                                  <option value="Prefer not to say">Prefer not to say</option>
                                </Select>
                              </Box>
                            </Flex>
                            <Flex direction={{ base: "column", md: "row" }} gap={4}>
                              <Box flex={1}>
                                <Text fontSize="xs" color={muted} mb={1}>
                                  {t("settings.fitness")}
                                </Text>
                                <Select
                                  size="sm"
                                  placeholder={t("settings.placeholderFitness")}
                                  name="workout"
                                  value={lifeStyleData.workout}
                                  onChange={handleLifeStyleChange}
                                  color={inputColor}
                                  bg={inputBg}
                                  borderColor={inputBorder}
                                  _focus={selectFocus}
                                  sx={nativeSelectOptionSx}
                                >
                                  <option value="Not active">Not active</option>
                                  <option value="occasionally">Occasionally</option>
                                  <option value="Active few times week">Active (a few times a week)</option>
                                  <option value="Very active">Very active (almost daily)</option>
                                  <option value="Fitness lifestyle">Fitness is my lifestyle</option>
                                  <option value="Prefer not to say">Prefer not to say</option>
                                </Select>
                              </Box>
                              <Box flex={1}>
                                <Text fontSize="xs" color={muted} mb={1}>
                                  {t("settings.religion")}
                                </Text>
                                <Select
                                  size="sm"
                                  placeholder={t("settings.placeholderReligion")}
                                  name="religion"
                                  value={lifeStyleData.religion}
                                  onChange={handleLifeStyleChange}
                                  color={inputColor}
                                  bg={inputBg}
                                  borderColor={inputBorder}
                                  _focus={selectFocus}
                                  sx={nativeSelectOptionSx}
                                >
                                  <option value="Christian  Catholic">Christian – Catholic</option>
                                  <option value="Christian  Protestant">Christian – Protestant</option>
                                  <option value="Christian  Other">Christian – Other</option>
                                  <option value="Muslim">Muslim</option>
                                  <option value="Jewish">Jewish</option>
                                  <option value="Hindu">Hindu</option>
                                  <option value="Buddhist">Buddhist</option>
                                  <option value="Spiritual but not religious">Spiritual but not religious</option>
                                  <option value="Agnostic">Agnostic</option>
                                  <option value="Atheist">Atheist</option>
                                  <option value="Other">Other</option>
                                  <option value="Prefer not to say">Prefer not to say</option>
                                </Select>
                              </Box>
                            </Flex>
                          </Flex>
                        </Box>

                        <Box {...sectionShellProps} bg={sectionTint}>
                          <Text {...sectionTitleProps}>{formatSectionLabel(t("settings.interests"))}</Text>
                          <Text fontSize="xs" color={muted} mb={3} lineHeight="tall">
                            {t("settings.interestsHint")}
                          </Text>
                          <Wrap gap={2} justify="flex-start">
                            {INTEREST_OPTIONS.map((interest) => (
                              <InterestEditPill
                                key={interest}
                                interest={interest}
                                updateState={handleUpdateInterests}
                                initialSelected={selectedInterests.has(interest)}
                              />
                            ))}
                          </Wrap>
                        </Box>

                        <Box {...sectionShellProps} bg={sectionTintBlue}>
                          <Text {...sectionTitleProps}>{formatSectionLabel(t("settings.profileImages"))}</Text>
                          <ImageUploader sendImagesData={handlePhotosChanged} minPhotos={2} />
                        </Box>
                      </Flex>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box
                    borderRadius="2xl"
                    borderWidth="1px"
                    borderColor={shellBorder}
                    bg={panelBg}
                    backdropFilter="blur(12px)"
                    overflow="hidden"
                    boxShadow={shellShadow}
                  >
                    <Flex px={{ base: 4, md: 6 }} py={4} borderBottomWidth="1px" borderColor={cardHeaderBorder} align="center" gap={2}>
                      <Icon
                        as={HiExternalLink}
                        color="brand.500"
                        boxSize={4}
                        display={{ base: "none", lg: "inline-block" }}
                      />
                      <Text fontSize="sm" fontWeight="semibold" color={titleColor}>
                        {t("settings.helpTitle")}
                      </Text>
                    </Flex>
                    <Box p={{ base: 4, md: 6 }}>
                      <Box bg={helpPanelBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={sectionBorder}>
                        <Text fontSize="sm" fontWeight="medium" color={titleColor} mb={2}>
                          {t("settings.needHelp")}
                        </Text>
                        <Text fontSize="xs" color={muted} mb={3}>
                          {t("settings.helpIntro")}
                        </Text>
                        <Box bg={helpInnerBg} p={3} borderRadius="md" borderWidth="1px" borderColor={sectionBorder}>
                          <Text fontSize="xs" fontWeight="medium" color={titleColor} mb={2}>
                            {t("settings.howToGetHelp")}
                          </Text>
                          <Box as="ul" fontSize="xs" color={muted} pl={4} style={{ listStyleType: "disc" }}>
                            <li>{t("settings.helpBullet1")}</li>
                            <li>{t("settings.helpBullet2")}</li>
                            <li>{t("settings.helpBullet3")}</li>
                            <li>{t("settings.helpBullet4")}</li>
                          </Box>
                        </Box>
                        <Box mt={4}>
                          <a href="https://www.instagram.com/payinapo/" target="_blank" rel="noopener noreferrer">
                            <Button
                              size="md"
                              width="100%"
                              rightIcon={isMobileShell ? undefined : <HiExternalLink />}
                              bgGradient="linear(to-r, brand.500, brand.600)"
                              color="white"
                              _hover={{ opacity: 0.92 }}
                            >
                              {t("settings.contactInstagram")}
                            </Button>
                          </a>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <SettingsMembership />
                </TabPanel>

                <TabPanel p={0}>
                  <Box
                    borderRadius="2xl"
                    borderWidth="1px"
                    borderColor={shellBorder}
                    bg={panelBg}
                    backdropFilter="blur(12px)"
                    overflow="hidden"
                    boxShadow={shellShadow}
                  >
                    <Flex px={{ base: 4, md: 6 }} py={4} borderBottomWidth="1px" borderColor={cardHeaderBorder} align="center" gap={2} color="red.500">
                      <Box display={{ base: "none", lg: "inline-flex" }}>
                        <Trash2 size={18} strokeWidth={2} />
                      </Box>
                      <Text fontSize="sm" fontWeight="semibold" color={titleColor}>
                        {t("settings.accountSettings")}
                      </Text>
                    </Flex>
                    <Box p={{ base: 4, md: 6 }}>
                      <Box bg={dangerPanelBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={dangerBorder}>
                        <Flex align="center" gap={2} mb={2}>
                          <Text fontSize="sm" fontWeight="medium" color={titleColor}>
                            {t("settings.deleteAccount")}
                          </Text>
                        </Flex>
                        <Text fontSize="xs" color={muted} mb={2}>
                          {t("settings.deleteIntro")}
                        </Text>
                        <Box as="ul" fontSize="xs" color={muted} pl={4} mb={4} style={{ listStyleType: "disc" }}>
                          <li>{t("settings.deleteBullet1")}</li>
                          <li>{t("settings.deleteBullet2")}</li>
                          <li>{t("settings.deleteBullet3")}</li>
                          <li>{t("settings.deleteBullet4")}</li>
                        </Box>
                        <Button
                          onClick={onOpen}
                          size="md"
                          width="100%"
                          bgGradient="linear(to-r, red.500, red.600)"
                          color="white"
                          _hover={{ opacity: 0.92 }}
                        >
                          {t("settings.deleteMyAccount")}
                        </Button>
                      </Box>
                    </Box>
                    <DeleteDialog isOpen={isOpen} onClose={onClose} cancelRef={cancelRef} />
                  </Box>
                </TabPanel>
              </TabPanels>
            </Box>
          </Flex>

          {isMobileShell && (
            <Drawer
              isOpen={isMenuOpen}
              placement="left"
              onClose={closeMenu}
              size={{ base: "xs", sm: "sm" }}
              autoFocus={false}
              returnFocusOnClose={false}
            >
              <DrawerOverlay backdropFilter="blur(8px)" />
              <DrawerContent
                bg={drawerContentBg}
                backdropFilter="blur(20px) saturate(160%)"
                sx={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
              >
                <DrawerCloseButton top={3} right={3} borderRadius="full" />
                <DrawerBody px={3} py={6} display="flex" flexDirection="column" gap={3}>
                  <Text
                    fontSize="11px"
                    fontWeight="semibold"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                    color={muted}
                    px={1}
                  >
                    {t("settings.settingsMenu")}
                  </Text>
                  {sidebarPanel}
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          )}
        </Tabs>
      </Box>

      {/* Mobile sticky bottom Save Changes bar — only on Profile Details tab */}
      {tabIndex === 0 && (
        <Box
          display={{ base: "block", lg: "none" }}
          position="fixed"
          left={0}
          right={0}
          zIndex={1099}
          bg={stickyBarBg}
          sx={{
            bottom: "calc(56px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <Box
            position="absolute"
            left={0}
            right={0}
            bottom="100%"
            h="56px"
            pointerEvents="none"
            bgGradient={stickyBarFadeGradient}
          />
          <Flex px={4} pt={3} pb={8}>
            <Button
              isLoading={isSaving}
              loadingText={t("common.saving")}
              isDisabled={!isProfileValid}
              flex="1"
              h="52px"
              fontWeight="semibold"
              borderRadius="full"
              bgGradient="linear(to-r, brand.500, brand.600)"
              color="white"
              boxShadow="0 6px 20px rgba(52,211,153,0.35), 0 10px 28px rgba(253,224,71,0.12)"
              _hover={{ opacity: 0.92 }}
              _active={{ transform: "scale(0.98)" }}
              _disabled={{ opacity: 0.5, cursor: "not-allowed", transform: "none" }}
              onClick={handleSaveChanges}
            >
              {t("common.saveChanges")}
            </Button>
          </Flex>
        </Box>
      )}
    </>
  );
};

export default Settings;
